// ══════════════════════════════════════════════════════════════════
//  낮 짐 → World 옵션 — **이음매의 알맹이이고, 순수 함수다.**
//
//  ■ 순수 함수인 이유가 전부다: **브라우저 없이 검사할 수 있어야 한다.**
//    이 매핑이 틀리면 「낮에서 뭘 하든 밤이 똑같다」가 되는데, 그건 화면을 봐서는
//    안 보인다. 그래서 `probe_bridge.mjs` 가 헤드리스로 같은 함수를 돌려 증명한다.
//
//  ■ ⚠ **왜 이 함수가 필요한가.** `gb/scene.js` 의 `create()` 는 World 를 이렇게 만든다:
//        new World(night, { nightKey, weapon, seed: 7, hp,
//                           flockN: FLOCK_START[key], baseLoss: BASE_LOSS[key],
//                           build, rescue })
//    **`seed` 가 리터럴 7 이고**, 낮이 보내는 `dayMods`·`dogs` 는
//    아예 안 넘어간다. 그런데 `World` 는 그것들을 **전부 읽는다**
//    (world.js 의 dayMods · dogs).
//    즉 문은 World 에 나 있고 씬이 그 앞을 막고 있다. 이 함수가 그 문으로 넣을 짐을 만든다.
//
//  ■ **밤 파일은 안 고친다.** 씬이 고쳐지는 날 이 파일은 그냥 얇아진다.
// ══════════════════════════════════════════════════════════════════
import { FLOCK_START, BASE_LOSS, NIGHTS } from '../gb/config.js';

// `pay`  = `toNightOpts(state, nightNo)` 산물 (낮이 싼 짐)
// `base` = 씬이 원래 넣던 값들 (무기·HP·빌드 등 — 낮이 모르는 실험실 설정)
export function worldOptsFrom(pay, base = {}) {
  const key = base.nightKey ?? pay?.nightKey;
  return {
    // ── 씬이 넣던 것 — 그대로 유지한다 ──────────────────────
    nightKey: key,
    weapon: pay?.weapon ?? base.weapon ?? 'pasu',
    hp: base.hp,
    build: base.build,
    baseLoss: BASE_LOSS[key],
    // ── 낮이 보낸 것 — 씬이 못 넘기던 자리들 ────────────────
    seed: pay?.seed,                              // 씬은 리터럴 7 이었다
    flockN: pay?.flockN ?? FLOCK_START[key],      // 씬은 config 표를 봤다
    // **낮 지갑 이월** — 이게 빠지면 밤이 낮에 모아 둔 치즈를 못 쓴다
    //   (사장님 지시 2026-07-29 「치즈는 모든 날 연속」).
    walletCarry: pay?.walletCarry ?? 0,
    lostSheep: pay?.lostSheep ?? 0,   // 마지막 밤 화살표 끝의 양 수
    rescue: pay?.rescue ?? base.rescue ?? false,
    dayMods: pay?.dayMods ?? null,                // ← 낮 3축이 여기로 들어간다(gb/dayin.js)
    dogs: pay?.dogs ?? undefined,                 // 오면 그것이 전부다(world.js:132)
    jackpot: pay?.jackpot ?? undefined,           // 귀한 돌 — gearshop 이 읽는다
    priorityMode: pay?.priorityMode ?? undefined,
    stabStart: pay?.stabStart ?? undefined,
    forcedHeal: pay?.forcedHeal ?? undefined,
  };
}

// **씬이 못 넘기던 자리들** — 이 목록이 곧 「이 이음매가 존재하는 이유」다.
// 검사기가 이걸 읽어 하나씩 대조한다.
// ⚠ **`retries` 가 여기 있었다** — 재시도 삭제(2026-08-03)로 낮이 더는 안 보낸다.
//   목록에 남겨 두면 브리지 검사가 「낮이 보낸 값과 밤이 받은 값이 다르다」로 터진다.
export const DAY_CARRIED = ['seed', 'flockN', 'dayMods', 'dogs', 'walletCarry', 'lostSheep', 'jackpot'];

// ══════════════════════════════════════════════════════════════════
//  씬에 넘기는 데이터 — **실험실이 아니라 여정의 값이다.**
//
//  ■ `gb/main.js`(실험실)는 무기·HP·배속·렌더를 사람이 고르게 하고, 그중 일부는
//    **씬 기본값과 다른 값**을 쓴다. 여정은 그걸 고를 화면이 없으므로 여기서 고정한다.
//    ⚠ 고정값을 **씬 기본에 맡기면 안 된다** — 씬 기본은 실험실이 「구 값」이라고
//      적어 둔 것들이 남아 있는 자리다. 실제로 넷이 어긋나 있었다:
//        hudMode    씬 'full'(계측 글 뭉치) ↔ 실험실 'bars'(플레이어 표시)
//        artScale   씬 1                    ↔ 실험실 1.3
//        showHearth 씬 false                ↔ 실험실 true (캠프 불)
//        cardSeed   씬 **고정 20260728**    ↔ 실험실 런마다 다름
//    마지막 것이 가장 나쁘다 — 고정이면 **모든 밤이 같은 3택을 내고, 재시도해도
//    같은 카드가 나온다.** 기도(재시도)의 값이 통째로 사라진다.
//
//  ■ ⚠ 이 값들은 **전부 정본 미기재**다. 낮이 모르는 값이므로 낮에서 보내지 않고
//    여기서 한 곳에 적는다. 정본이 정하면 이 표만 고친다.
// ══════════════════════════════════════════════════════════════════

// 여정의 밤이 고정으로 쓰는 것 — 실험실의 **플레이어 표시** 기본값을 따른다.
export const JOURNEY_FIXED = {
  speed: 1,             // 배속은 계측용이다. 여정은 등속
  cardMode: 'real',     // 3택 1을 사람이 고른다(실험실 기본과 같다)
  sound: true,
  render: 'sprite',
  worldBuild: 'ramp',   // 레벨업이 실제로 무기에 먹힌다(10-B 6-2)
  build: 'none',
  effect: 'soft',
  hp: 350,              // 10-B 1 확정. 씬 기본과 같지만 **적어 둔다** — 씬이 바뀌어도 안 흔들리게
  artScale: 1.3,        // 실험실 플레이어 표시값. 씬 기본 1 은 판정과 무관한 구 값이다
  viewZoom: 1,          // 1.0 을 넘기면 경기장 가장자리가 잘린다(헌법 5조 「다 보인다」)
  a11y: false,
  forceFixed: false,
};
// 씬을 만든 **뒤에** 인스턴스에 얹는 것들(init 이 안 읽는다 — main.js 도 같은 방식이다).
export const JOURNEY_AFTER = {
  hudMode: 'bars',      // 플레이어 표시. 'full' 은 계측 글 뭉치다
  showRadii: false,     // 판정 반경 오버레이 — 계측용
  showHearth: true,     // 캠프 불티
};

// **카드 시드는 밤마다·재시도마다 달라야 한다.**
//   낮의 `seed` 는 이미 (뿌리, 밤, 재시도)를 담고 있으므로 거기서 파생한다.
//   섞는 이유: 같은 수를 쓰면 카드 뽑기와 스폰 난수가 같은 자리에서 출발한다.
//   ⚠ **파생 규칙은 정본 미기재**이며 여기 한 줄이 전부다.
export const cardSeedFrom = (seed) => ((((seed ?? 0) >>> 0) ^ 0x9e3779b9) >>> 0) || 1;

// 씬 `init(d)` 에 그대로 넣을 데이터.
export function sceneDataFrom(pay) {
  return {
    ...JOURNEY_FIXED,
    night: pay?.nightKey,
    weapon: pay?.weapon ?? 'pasu',
    rescue: pay?.rescue ?? false,
    cardSeed: cardSeedFrom(pay?.seed),
    dayOpts: pay,        // ← 가로채기가 읽는다(World 를 낮 짐으로 다시 만든다)
  };
}

// 직접 진입 진단도 실제 날짜 순서로 보여야 한다. `buildNights()`는 초안 밤을 나중에
// 삽입하므로 Object.keys의 원순서를 그대로 노출하면 N4 등이 N40 뒤로 밀린다.
export const isPlayable = (nightKey) => !!NIGHTS[nightKey];
export const PLAYABLE_KEYS = Object.keys(NIGHTS)
  .sort((a, b) => Number(a.slice(1)) - Number(b.slice(1)));
